/*
  <bold-variants> — pretty Bold variant pills that PROXY the theme's real picker.

  WHY A PROXY (and not a parallel picker):
  The theme's own <variant-selects> stays in the DOM (visually hidden) as the
  SINGLE source of truth. product-info.js listens to its radios'/select's
  `change` events to swap the variant, price and media and to keep the cart form
  in sync. This element never holds variant state of its own: a pill click is
  forwarded onto the matching native input (which fires the real `change`), and
  the visible pills are re-reflected by listening to PUB_SUB_EVENTS.variantChange
  (published by product-info.js with the resolved variant). That keeps cart and
  variant mechanics 100% intact.

  Guarded (no double-define), keyed to its section via data-section, defer-loaded.
  No animation — prefers-reduced-motion is irrelevant here.
*/
if (!customElements.get('bold-variants')) {
  customElements.define(
    'bold-variants',
    class BoldVariants extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section || '';

        // The theme's real picker lives in the same <product-info>, wrapped in
        // .bold-hero__native-picker. Scope all native lookups to it.
        this.productInfo = this.closest('product-info');
        this.nativePicker =
          (this.productInfo &&
            this.productInfo.querySelector('.bold-hero__native-picker')) ||
          (this.productInfo &&
            this.productInfo.querySelector('variant-selects')) ||
          null;

        this.pills = Array.from(this.querySelectorAll('[data-bold-value]'));

        this.onPillClick = this.onPillClick.bind(this);
        this.pills.forEach((pill) => pill.addEventListener('click', this.onPillClick));

        // Reflect selection whenever the real picker resolves a new variant.
        this.onVariantChange = this.onVariantChange.bind(this);
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(
            PUB_SUB_EVENTS.variantChange,
            this.onVariantChange
          );
        }

        // Tint the column glyphs to the selected frame colour on first paint.
        this.syncColumnFinish();
      }

      disconnectedCallback() {
        this.pills.forEach((pill) => pill.removeEventListener('click', this.onPillClick));
        this.variantChangeUnsubscriber?.();
      }

      onPillClick(event) {
        const pill = event.currentTarget;
        const optionPosition = pill.getAttribute('data-bold-opt');
        const value = pill.getAttribute('data-bold-value');
        if (!optionPosition || value == null) return;

        const changed = this.setNativeOption(optionPosition, value);

        // Optimistically reflect within this option group so the UI feels
        // instant; variantChange will confirm/correct shortly after.
        if (changed) this.reflectGroupSelection(optionPosition, value);
        this.syncColumnFinish();
      }

      /*
        Find the native control for an option position and set it to `value`,
        then dispatch a bubbling `change` so product-info.js reacts.
        Returns true when a control was found and updated.
      */
      setNativeOption(optionPosition, value) {
        const root = this.nativePicker;
        if (!root) return false;

        // Radio/button picker. The theme's radio group `name` can carry trailing
        // whitespace ("Dimensiune blat-2\n"), so we can't rely on an exact
        // "-<position>" suffix. Each option value is unique across the picker, so
        // match by value (trimmed); prefer a radio whose (trimmed) group name ends
        // with "-<position>" when several share a value.
        const want = String(value).trim();
        const radios = Array.from(root.querySelectorAll('input[type="radio"]'));
        const byValue = radios.filter((input) => String(input.value).trim() === want);

        if (byValue.length) {
          const inGroup = byValue.find((input) =>
            (input.getAttribute('name') || '').trim().endsWith('-' + optionPosition)
          );
          const target = inGroup || byValue[0];
          if (target.checked) return false; // already selected
          target.checked = true;
          target.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }

        // Dropdown picker: <select> in document order matches option position.
        const selects = Array.from(root.querySelectorAll('select'));
        const select = selects[Number(optionPosition) - 1];
        if (select) {
          const hasValue = Array.from(select.options).some((o) => o.value === value);
          if (!hasValue) return false;
          if (select.value === value) return false;
          select.value = value;
          select.dispatchEvent(new Event('change', { bubbles: true }));
          return true;
        }

        return false;
      }

      reflectGroupSelection(optionPosition, value) {
        const groupPills = this.pills.filter(
          (pill) => pill.getAttribute('data-bold-opt') === String(optionPosition)
        );
        groupPills.forEach((pill) => {
          const isSelected = pill.getAttribute('data-bold-value') === value;
          pill.classList.toggle('is-selected', isSelected);
          pill.setAttribute('aria-checked', isSelected ? 'true' : 'false');
          pill.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
        });
      }

      onVariantChange(payload) {
        const data = payload && payload.data ? payload.data : {};
        if (String(data.sectionId) !== String(this.sectionId)) return;
        const variant = data.variant;
        if (!variant || !Array.isArray(variant.options)) return;

        // variant.options is ordered by option position (index 0 => position 1).
        variant.options.forEach((optionValue, index) => {
          this.reflectGroupSelection(index + 1, optionValue);
          this.updateSelectedLabel(index + 1, optionValue);
        });
        this.syncColumnFinish();
      }

      updateSelectedLabel(optionPosition, value) {
        const label = this.querySelector(
          '[data-bold-selected-label="' + optionPosition + '"]'
        );
        if (label) label.textContent = value;
      }

      /*
        Tint the column-picker glyphs to the currently selected frame colour
        (white/black) so a column reads as "in your chosen frame finish" rather
        than implying a colour of its own. Reads the selected frame pill's hex
        (data-bold-hex, locale-independent) and exposes CSS vars the column shapes
        consume. No-op when the product has no frame-colour option.
      */
      syncColumnFinish() {
        const frameGroup = this.querySelector('[data-bold-frame]');
        if (!frameGroup) return;
        const selected =
          frameGroup.querySelector('.bold-hero__pill.is-selected[data-bold-hex]') ||
          frameGroup.querySelector('[data-bold-hex]');
        const hex = selected && selected.getAttribute('data-bold-hex');
        if (!hex) return;
        this.style.setProperty('--bold-col-finish', hex);
        this.style.setProperty(
          '--bold-col-ring',
          this.isLightHex(hex) ? 'rgba(0,0,0,0.22)' : 'rgba(0,0,0,0.12)'
        );
      }

      isLightHex(hex) {
        const h = String(hex).replace('#', '');
        if (h.length < 6) return false;
        const r = parseInt(h.slice(0, 2), 16);
        const g = parseInt(h.slice(2, 4), 16);
        const b = parseInt(h.slice(4, 6), 16);
        return 0.299 * r + 0.587 * g + 0.114 * b > 150;
      }
    }
  );
}
